import type { AccountingTotals } from "../pages/Accounting/AccountingStats";

export type BillingCategory = keyof AccountingTotals;

export const BILLING_CATEGORY_LABELS: Record<BillingCategory, string> = {
  initiation: "Initiation",
  annualDues: "Annual Dues",
  assessmentDues: "Assessment Dues",
  membershipBalance: "Membership Balance",
  specialFund: "Special Fund",
  annualMeeting: "Annual Meeting",
  lateFees: "Late Fees",
};

/** Stable order for payment allocation rows and similar UIs. */
export const BILLING_CATEGORY_DISPLAY_ORDER: BillingCategory[] = [
  "initiation",
  "annualDues",
  "membershipBalance",
  "assessmentDues",
  "specialFund",
  "annualMeeting",
  "lateFees",
];

const CODE_TO_CATEGORY: Record<string, BillingCategory> = {
  annual: "annualDues",
  previousBalance: "membershipBalance",
  assessment: "assessmentDues",
  special: "specialFund",
  contingency: "specialFund",
  annualMeeting: "annualMeeting",
  late: "lateFees",
  initiation: "initiation",
};

const KIND_TO_CATEGORY: Record<string, BillingCategory> = {
  membershipDues: "annualDues",
  assessment: "assessmentDues",
  special: "assessmentDues",
  contingency: "specialFund",
  annualMeeting: "annualMeeting",
  lateFee: "lateFees",
};

export type BillingCategoryInvoice = {
  context?: { kind?: string; fiscalYear?: number };
};

export type BillingCategoryItem = {
  code?: string;
  description?: string;
  amount?: number;
  quantity?: number;
  paid?: number;
};

const ASSESSMENT_INVOICE_KINDS = new Set(["assessment", "special"]);

export function resolveItemCategory(
  item: BillingCategoryItem,
  invoice: BillingCategoryInvoice,
): BillingCategory | undefined {
  const code = item.code ?? "";
  const invoiceKind = invoice.context?.kind ?? "";

  if (code === "previousBalance") {
    return "membershipBalance";
  }

  if (code === "assessment") {
    return "assessmentDues";
  }

  // Legacy standalone assessment invoices used code "special" at invoice level.
  if (code === "special" && ASSESSMENT_INVOICE_KINDS.has(invoiceKind)) {
    return "assessmentDues";
  }

  if (code && CODE_TO_CATEGORY[code]) {
    return CODE_TO_CATEGORY[code];
  }

  if (invoiceKind && KIND_TO_CATEGORY[invoiceKind]) {
    return KIND_TO_CATEGORY[invoiceKind];
  }

  return undefined;
}

export function resolveInvoiceCategory(
  invoice: BillingCategoryInvoice,
): BillingCategory | undefined {
  if (invoice.context?.kind && KIND_TO_CATEGORY[invoice.context.kind]) {
    return KIND_TO_CATEGORY[invoice.context.kind];
  }
  return undefined;
}

export function getBillingCategoryLabel(
  category: BillingCategory,
): string {
  return BILLING_CATEGORY_LABELS[category];
}
